import React, { useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { PlatformDetail } from 'api'
import { ModalWindow, ModalFooter, ModalContent } from 'view/components/panels'
import { Tabs } from 'view/components/tabs'
import { TextButtonType, TextButton } from 'view/components/buttons'
import { platformDetailSelector } from 'selectors/platformSelectors'
import { DiagnosticsActions } from 'epics/diagnosticsEpic'
import { Translation, Locale } from 'locale'
import { isOpenFin } from 'utils'
import { DialogRow, DialogField } from 'view/components/inputs'

interface AboutDialogProps {
  platformDetails: PlatformDetail | null
  sendDiagnostics: () => unknown
  onClose?: () => unknown
}

const mapStateToProps = createStructuredSelector({
  platformDetails: platformDetailSelector
})

const mapDispatchToProps = {
  sendDiagnostics: DiagnosticsActions.sendDiagnostics
}

const Content = styled(ModalContent)`
  justify-content: center;
  padding: 20px 20px 0 20px;
  height: 60px;
`

const AboutDialogComponent: React.FunctionComponent<AboutDialogProps> = props => {
  const { platformDetails, sendDiagnostics, onClose } = props
  const [selectedTab, setSelectedTab] = useState(Translation.TC_OVERVIEW)
  const [openFinRuntimeVersion, setOpenFinRuntimeVersion] = useState<String | null>(null)

  const onChangeTab = useCallback((tab: Translation) => {
    setSelectedTab(tab)
  }, [])

  useEffect(() => {
    if (isOpenFin()) {
      ;(async () => {
        setOpenFinRuntimeVersion(await fin.System.getVersion())
      })()
    }
  })

  const renderOverview = useCallback(() => {
    return (
      <>
        <DialogRow>
          <DialogField>
            <strong>{Locale.translate(Translation.TC_VERSION)}</strong>
          </DialogField>
          <DialogField flex={2}>{platformDetails && platformDetails.platform.applicationVersion}</DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <strong>{Locale.translate(Translation.TC_BUILD)}</strong>
          </DialogField>
          <DialogField flex={2}>{BUILD_COMMIT}</DialogField>
        </DialogRow>
        {openFinRuntimeVersion && (
          <DialogRow>
            <DialogField>
              <strong>{Locale.translate(Translation.TC_OPEN_FIN_VERSION)}</strong>
            </DialogField>
            <DialogField flex={2}>{openFinRuntimeVersion}</DialogField>
          </DialogRow>
        )}
      </>
    )
  }, [platformDetails, openFinRuntimeVersion])

  const renderDiagnostics = useCallback(() => {
    return (
      <TextButton type={TextButtonType.Secondary} onClick={sendDiagnostics}>
        {Locale.translate(Translation.TC_ACTION_SEND_DIAGNOSTICS)}
      </TextButton>
    )
  }, [sendDiagnostics])

  return (
    <ModalWindow title={Locale.translate(Translation.TC_ABOUT)}>
      <Tabs selectedTab={selectedTab} onChangeTab={onChangeTab} tabs={[Translation.TC_OVERVIEW, Translation.TC_DIAGNOSTICS]} />
      <Content>
        {selectedTab === Translation.TC_OVERVIEW && renderOverview()}
        {selectedTab === Translation.TC_DIAGNOSTICS && renderDiagnostics()}
      </Content>

      <ModalFooter>
        <TextButton type={TextButtonType.Primary} onClick={onClose}>
          {Locale.translate(Translation.TC_BUTTON_OK)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const AboutDialog = connect(mapStateToProps, mapDispatchToProps)(AboutDialogComponent)
